// ============================================================================
// CE QUE LES CAISSES ONT REÇU DES RÉGLAGES — dit en clair, sur la page Magasin et dans l'en-tête de Paramètres.
//
// UTILITÉ : la seule question qu'on se pose après « Enregistrer » est « les caisses l'ont-elles ? ». Le serveur juge
// chaque caisse (il est seul à savoir lire sa position) ; ce module dit le jugement avec les mots du dessin, une fois
// pour les deux écrans qui le montrent.
// ⚠️ PLUS DE NUMÉRO DE RÉVISION À L'ÉCRAN (décision du dessin) : il ne parlait qu'à qui connaît le journal.
// ============================================================================
import type { StoreTopology } from '../api';
import { key, t, type Translatable } from '../i18n';

type Delivery = 'up_to_date' | 'pending' | 'never';

/** Le mot et la teinte de chaque état — ceux du dessin. */
export const DELIVERY_VIEW: Record<Delivery, { label: Translatable; bg: string; fg: string }> = {
  up_to_date: { label: key('Réglages à jour'), bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  pending: { label: key('Réglages en attente'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  never: { label: key('Jamais connectée'), bg: 'var(--ink-100)', fg: 'var(--ink-500)' },
};

/**
 * Quand le dernier réglage a été enregistré, en peu de mots.
 * ⚠️ LES UNITÉS SONT ABRÉGÉES, et ce ne sont pas des phrases comptées : « min », « h » et « j » sont des symboles
 *    invariables, d'où un trou qui ne s'appelle pas `{n}`.
 */
export function savedAgo(at: number, now: number): string {
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  if (minutes < 1) return t('Enregistré à l’instant');
  if (minutes < 60) return t('Enregistré il y a {delai} min', { delai: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return t('Enregistré il y a {delai} h', { delai: hours });
  return t('Enregistré il y a {delai} j', { delai: Math.floor(hours / 24) });
}

/**
 * LE RÉSUMÉ DE L'EN-TÊTE DE PARAMÈTRES — « Enregistré il y a 6 min · 2 caisses à jour sur 3 · 1 en attente ».
 *
 * ⚠️ UNE CAISSE JAMAIS CONNECTÉE N'EST PAS COMPTÉE : elle n'a jamais rien reçu, et la compter dans « sur 3 » ferait
 *    croire à un retard que la synchronisation rattrapera (dessin).
 * ⚠️ CE QUI N'EST PAS MESURÉ SE TAIT : registre ou journal illisible, la part des caisses disparaît plutôt que
 *    d'annoncer zéro. `null` = rien à dire du tout.
 */
export function deliverySummary(topology: StoreTopology, now: number): { text: string; pending: boolean } | null {
  const parts: string[] = [];
  if (topology.settingsSavedAt !== null) parts.push(savedAgo(topology.settingsSavedAt, now));
  const judged = topology.registers?.map((r) => r.settings) ?? [];
  const pending = judged.filter((s) => s === 'pending').length;
  if (topology.registers !== null && judged.every((s) => s !== null)) {
    const connected = judged.filter((s) => s !== 'never').length;
    if (connected === 0) parts.push(t('aucune caisse connectée'));
    else {
      parts.push(t('{n} caisse à jour sur {total}', { n: connected - pending, total: connected }));
      if (pending > 0) parts.push(t('{n} en attente', { n: pending }));
    }
  }
  return parts.length ? { text: parts.join(' · '), pending: pending > 0 } : null;
}
